import React from "react";
import { Helmet } from "react-helmet-async";

/**
 * SEOHead — Reusable per-page SEO component for medev.in
 * Handles dynamic title, meta description, OG tags, Twitter cards,
 * canonical URL, and JSON-LD structured data per route.
 */
const SEOHead = ({
  title,
  description,
  canonicalPath = "/",
  pageType = "WebPage",
  ogImage = "https://medev.in/og-image.png",
  keywords,
  noIndex = false,
  structuredData = null,
}) => {
  const siteUrl = "https://medev.in";
  const fullUrl = `${siteUrl}${canonicalPath}`;
  const siteName = "Medev.in";

  const defaultStructuredData = {
    "@context": "https://schema.org",
    "@type": pageType,
    "@id": `${fullUrl}#webpage`,
    "name": title,
    "description": description,
    "url": fullUrl,
    "isPartOf": {
      "@id": "https://medev.in/#website"
    },
    "inLanguage": "en-IN",
    "potentialAction": {
      "@type": "ReadAction",
      "target": [fullUrl]
    }
  };

  const jsonLd = structuredData || defaultStructuredData;

  return (
    <Helmet>
      {/* Primary SEO */}
      <title>{title}</title>
      <meta name="description" content={description} />
      {keywords && <meta name="keywords" content={keywords} />}
      <meta name="robots" content={noIndex ? "noindex, nofollow" : "index, follow, max-snippet:-1, max-image-preview:large"} />

      {/* Canonical */}
      <link rel="canonical" href={fullUrl} />

      {/* Open Graph */}
      <meta property="og:type" content="website" />
      <meta property="og:url" content={fullUrl} />
      <meta property="og:site_name" content={siteName} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:alt" content={`${siteName} — ${title}`} />
      <meta property="og:locale" content="en_IN" />

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content="@medevin" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />
      <meta name="twitter:image:alt" content={`${siteName} — ${title}`} />

      {/* JSON-LD Structured Data */}
      <script type="application/ld+json">
        {JSON.stringify(jsonLd, null, 2)}
      </script>
    </Helmet>
  );
};

// ==================== PER-PAGE SEO CONFIGS ====================
// Export ready-made SEO configs for each page

export const HomeSEO = () => (
  <SEOHead
    title="Medev.in | Premium Web & Mobile Development Agency — React, Next.js, Flutter | Delhi, India"
    description="Medev.in is a premium full-stack web and mobile development agency in Delhi, India. We craft high-performance React, Next.js & Flutter apps. Get a quote today."
    canonicalPath="/"
    pageType="WebPage"
    keywords="web development agency delhi, full stack developer india, react developer delhi, next.js development, flutter developer, mobile app development delhi, medev.in, hire web developer india, software development company"
    structuredData={{
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "WebPage",
          "@id": "https://medev.in/#homepage",
          "url": "https://medev.in/",
          "name": "Medev.in | Premium Web & Mobile Development Agency",
          "isPartOf": { "@id": "https://medev.in/#website" },
          "about": { "@id": "https://medev.in/#organization" },
          "description": "Premium full-stack development agency in Delhi, India. React, Next.js, Flutter, Node.js.",
          "inLanguage": "en-IN",
          "breadcrumb": {
            "@type": "BreadcrumbList",
            "itemListElement": [
              {
                "@type": "ListItem",
                "position": 1,
                "name": "Home",
                "item": "https://medev.in/"
              }
            ]
          }
        }
      ]
    }}
  />
);

export const ServicesSEO = () => (
  <SEOHead
    title="Web Development Services | React, Next.js, Flutter, Node.js — Medev.in Delhi"
    description="Expert web development, mobile app development, backend APIs and UI/UX design services. Full-stack React, Next.js, Flutter development in Delhi, India. Get your project started."
    canonicalPath="/services"
    pageType="WebPage"
    keywords="web development services delhi, react development services, next.js development agency, flutter mobile app development, node.js backend development, ui ux design services india, full stack development services"
    structuredData={{
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "WebPage",
          "@id": "https://medev.in/services#webpage",
          "url": "https://medev.in/services",
          "name": "Development Services — Medev.in",
          "isPartOf": { "@id": "https://medev.in/#website" },
          "description": "Web, mobile, backend and UI/UX development services from Medev.in",
          "inLanguage": "en-IN",
          "breadcrumb": {
            "@type": "BreadcrumbList",
            "itemListElement": [
              { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://medev.in/" },
              { "@type": "ListItem", "position": 2, "name": "Services", "item": "https://medev.in/services" }
            ]
          }
        },
        {
          "@type": "Service",
          "name": "Web Development Services",
          "provider": { "@id": "https://medev.in/#organization" },
          "serviceType": "Full-Stack Web Development",
          "description": "Premium React, Next.js, and modern web application development",
          "areaServed": { "@type": "Country", "name": "India" }
        },
        {
          "@type": "Service",
          "name": "Mobile App Development Services",
          "provider": { "@id": "https://medev.in/#organization" },
          "serviceType": "Mobile App Development",
          "description": "Cross-platform Flutter and native iOS mobile application development",
          "areaServed": { "@type": "Country", "name": "India" }
        }
      ]
    }}
  />
);

export const WorkSEO = () => (
  <SEOHead
    title="Our Portfolio & Recent Work | Web & Mobile Projects — Medev.in"
    description="Browse Medev.in's portfolio of premium web and mobile development projects. React apps, Next.js websites, Flutter mobile apps built for clients across India and globally."
    canonicalPath="/work"
    pageType="CollectionPage"
    keywords="web development portfolio, react project portfolio, mobile app projects, software development portfolio india, medev.in projects, web agency portfolio delhi"
    structuredData={{
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      "@id": "https://medev.in/work#webpage",
      "url": "https://medev.in/work",
      "name": "Portfolio — Medev.in",
      "description": "Recent web and mobile development projects by Medev.in",
      "isPartOf": { "@id": "https://medev.in/#website" },
      "inLanguage": "en-IN",
      "breadcrumb": {
        "@type": "BreadcrumbList",
        "itemListElement": [
          { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://medev.in/" },
          { "@type": "ListItem", "position": 2, "name": "Work", "item": "https://medev.in/work" }
        ]
      }
    }}
  />
);

export const ProcessSEO = () => (
  <SEOHead
    title="Our Development Process | Discovery to Launch — Medev.in"
    description="See how Medev.in delivers premium software: Discovery, Design & Planning, Development, Testing & Launch. A proven 4-step process for high-quality digital products."
    canonicalPath="/process"
    pageType="WebPage"
    keywords="software development process, agile web development, development workflow, how web development works, development agency process india"
    structuredData={{
      "@context": "https://schema.org",
      "@type": "WebPage",
      "@id": "https://medev.in/process#webpage",
      "url": "https://medev.in/process",
      "name": "Development Process — Medev.in",
      "description": "Our 4-step discovery-to-launch development process",
      "isPartOf": { "@id": "https://medev.in/#website" },
      "inLanguage": "en-IN",
      "breadcrumb": {
        "@type": "BreadcrumbList",
        "itemListElement": [
          { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://medev.in/" },
          { "@type": "ListItem", "position": 2, "name": "Process", "item": "https://medev.in/process" }
        ]
      }
    }}
  />
);

export const ContactSEO = () => (
  <SEOHead
    title="Hire a Web Developer in Delhi | Contact Medev.in — Get a Free Quote"
    description="Ready to build something great? Contact Medev.in — Delhi's premium web & mobile development agency. Email mmkumar581@gmail.com or call +91 9811766762. Free consultation."
    canonicalPath="/contact"
    pageType="ContactPage"
    keywords="hire web developer delhi, contact web development agency, get web development quote, hire react developer india, software development inquiry, medev.in contact"
    structuredData={{
      "@context": "https://schema.org",
      "@type": "ContactPage",
      "@id": "https://medev.in/contact#webpage",
      "url": "https://medev.in/contact",
      "name": "Contact Medev.in — Get a Free Quote",
      "description": "Contact Medev.in for web and mobile development services",
      "isPartOf": { "@id": "https://medev.in/#website" },
      "inLanguage": "en-IN",
      "breadcrumb": {
        "@type": "BreadcrumbList",
        "itemListElement": [
          { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://medev.in/" },
          { "@type": "ListItem", "position": 2, "name": "Contact", "item": "https://medev.in/contact" }
        ]
      }
    }}
  />
);

export default SEOHead;
